import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { Participation, ParticipationFirestore, Players } from "../../types";
import { Tableau, tableauFromFirestoreData, normalizeTableauFromCallable } from "../../types";
import {
  isRegistered,
  isDisabled,
  getEffectiveSelectedBoardIds,
  hasIncompatiblePairAmong,
  isDailySelectionValid,
} from "./playerHelper";
import { groupBy } from "../../helpers/arrayHelper";
import cloudFunctions from "../../services/firebaseCloudFunctions";
import { httpsCallable } from "firebase/functions";
import { FirebaseError } from "firebase/app";
import {
  collection,
  getDoc,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import firestoreInstance from "../../services/firestoreInstance";
import { registrationPriceFromBoards } from "../../constants/tournamentPricing";
import { calendarDayKey } from "./playerHelper";

type BoardDynamicParams = {
  selected: boolean;
  selectable: boolean;
  disabled: boolean;
};

export type AccessibleBoard = Tableau & BoardDynamicParams;

export type PlayerState = {
  player: Players | null;
  status: "idle" | "succeeded" | "loading" | "failed" | "noplayer";
  paymentStatus: "idle" | "succeeded" | "loading" | "failed" | "noplayer";
  error: string;
  accessibleBoards: Tableau[];
  selectedBoardIds: string[];
  unselectedBoardIds: string[];
  playersWithRegistrations: Players[];
  players: Players[];
  playersWithRegistrationsLoading: boolean;
  allowAdminDailyLimitBypass: boolean;
  /** Admin uniquement : contourner la règle places restantes vs liste d'attente à la sauvegarde. */
  allowAdminWaitlistBypass: boolean;
  saveRegistrationsStatus: "idle" | "loading" | "failed";
};

const initialState: PlayerState = {
  player: null,
  status: "idle",
  paymentStatus: "idle",
  error: "",
  accessibleBoards: [],
  selectedBoardIds: [],
  unselectedBoardIds: [],
  playersWithRegistrations: [],
  players: [],
  playersWithRegistrationsLoading: false,
  allowAdminDailyLimitBypass: false,
  allowAdminWaitlistBypass: false,
  saveRegistrationsStatus: "idle",
};

export const fetchPlayerFromLicence = createAsyncThunk<
  any,
  string,
  { state: RootState }
>("player/fetchPlayer", async (licenceId: string, thunkApi) => {
  const findPlayerFromLicence = httpsCallable<unknown, Players>(
    cloudFunctions,
    "findplayerfromlicence"
  );

  try {
    const tournamentId = thunkApi.getState().tournoi.tournoi?.id;
    if (!tournamentId) {
      throw new Error("Tournoi courant introuvable.");
    }
    const response = await findPlayerFromLicence({ licenceId, tournamentId });
    return response.data as Players;
  } catch (error: unknown) {
    if (
      error instanceof FirebaseError &&
      error.code === "functions/not-found"
    ) {
      return null;
    }
    throw error;
  }
});

export const fetchPlayersWithRegistrations = createAsyncThunk<
  Players[],
  void,
  { state: RootState }
>("player/fetchPlayersWithRegistrations", async (_, thunkApi) => {
  const db = firestoreInstance;

  // 1️⃣ Charger tous les joueurs
  const playersSnap = await getDocs(collection(db, "players"));
  const playersMap = new Map<string, Players>();
  playersSnap.docs.forEach((doc) => {
    playersMap.set(doc.id, {
      ...(doc.data() as Players),
      id: doc.id,
      participation: [],
    });
  });

  // 2️⃣ Charger tous les tableaux
  const currentTournamentId = thunkApi.getState().tournoi.tournoi?.id;
  if (!currentTournamentId) {
    throw new Error("Tournoi courant introuvable.");
  }
  const tableauxSnap = await getDocs(
    collection(db, "tournois", currentTournamentId, "tableaux")
  );
  const tableauxMap = new Map<string, Tableau>();
  tableauxSnap.docs.forEach((d) => {
    tableauxMap.set(
      d.id,
      tableauFromFirestoreData(d.id, d.data() as Record<string, unknown>)
    );
  });

  // 3️⃣ Charger toutes les participations
  const participationSnap = await getDocs(
    collection(db, "tournois", currentTournamentId, "participation")
  );
  const privateQueue: { ref: any; target: Participation }[] = [];

  const participationList: Participation[] = participationSnap.docs
    .map((docSnap) => {
      const data = docSnap.data() as ParticipationFirestore;
      const tableau = tableauxMap.get(data.tableauId);
      if (!tableau) return null; // Élimine les participations sans tableau
      const participation = {
        id: docSnap.id,
        ...data,
        dateInscription: data.dateInscription?.toDate(),
        checkedInAt: data.checkedInAt?.toDate?.() ?? data.checkedInAt,
        tableau, // Toujours un tableau présent
      } as Participation;

      privateQueue.push({
        ref: doc(
          firestoreInstance,
          "tournois",
          currentTournamentId,
          "participation",
          docSnap.id,
          "private",
          "data"
        ),
        target: participation,
      });

      return participation;
    })
    .filter((p): p is Participation => p !== null); // ⬅️ TypeScript infère le type correct

  // 4️⃣ Enrichir les participations avec private data
  const BATCH_SIZE = 100;
  for (let i = 0; i < privateQueue.length; i += BATCH_SIZE) {
    const batch = privateQueue.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async ({ ref, target }) => {
        const snap = await getDoc(ref);
        const privateData = snap.exists()
          ? (snap.data() as { email?: string; paymentStatus?: string })
          : {};
        target.email = privateData.email ?? undefined;
        const allowedStatus = [
          "registration",
          "admin",
          "offert",
          "cancelled",
          "refunded",
        ] as const;
        target.paymentStatus = allowedStatus.includes(
          privateData.paymentStatus as (typeof allowedStatus)[number]
        )
          ? (privateData.paymentStatus as (typeof allowedStatus)[number])
          : undefined;
      })
    );
  }

  // 5️⃣ Associer participations aux joueurs
  participationList.forEach((participation) => {
    const player = playersMap.get(participation.playersId);
    if (player) {
      player.participation?.push(participation);
    }
  });

  // 6️⃣ Retourner les joueurs ayant au moins une participation (tableau ou non)
  return Array.from(playersMap.values()).filter(
    (p) => p.participation && p.participation.length > 0
  );
});

export const fetchPlayers = createAsyncThunk<
  Players[],
  void,
  { state: RootState }
>("player/fetchPlayers", async (_, thunkApi) => {
  const players = (
    await getDocs(
      query(
        collection(firestoreInstance, "players"),
        orderBy("name"),
        orderBy("surname")
      )
    )
  ).docs.map((doc) => ({ ...(doc.data() as Players), ...{ id: doc.id } }));
  return players as Players[];
});

export const fetchAccessibleBoards = createAsyncThunk<
  Tableau[],
  string,
  { state: RootState }
>("player/fetchAccessibleBoards", async (playerId: string, thunkApi) => {
  const currentTournamentId = thunkApi.getState().tournoi.tournoi?.id;

  const accessibleBoards = httpsCallable<unknown, Tableau[]>(
    cloudFunctions,
    "accessibleBoards"
  );

  const response = await accessibleBoards({
    playerId,
    tournamentId: currentTournamentId,
  });

  const boards = response.data
    .map((board: unknown) => normalizeTableauFromCallable(board))
    // Inscription sur place : hors parcours en ligne (info uniquement sur /register).
    .filter((board) => board.inscriptionSurPlace !== true);

  return boards.sort((a, b) => a.name.localeCompare(b.name));
});

export type SaveRegistrationsOptions = {
  /** Si true, pointe automatiquement les nouveaux tableaux dont la date est aujourd'hui. */
  autoCheckInTodayBoards?: boolean;
};

export const saveRegistrations = createAsyncThunk<
  unknown,
  SaveRegistrationsOptions | void,
  { state: RootState }
>("player/saveRegistrations", async (options, { getState }) => {
  const selectedBoardIds = getState().player.selectedBoardIds;
  const unselectedBoardIds = getState().player.unselectedBoardIds;
  const playerId = getState().player.player?.id;
  const isRegistrationPhaseClosed =
    (getState().tournoi.tournoi?.registrationPhase ?? "open") === "closedForPlayers";
  const allowAdminDailyLimitBypass =
    getState().player.allowAdminDailyLimitBypass;
  const allowAdminWaitlistBypass =
    getState().player.allowAdminWaitlistBypass;
  const autoCheckInTodayBoards = options?.autoCheckInTodayBoards === true;

  const saveRegistrationsCallable = httpsCallable<unknown, unknown>(
    cloudFunctions,
    "saveRegistrations"
  );

  const response = await saveRegistrationsCallable({
    tournamentId: getState().tournoi.tournoi?.id,
    playerId,
    selectedBoardIds,
    unselectedBoardIds,
    isRegistrationPhaseClosed,
    allowAdminDailyLimitBypass,
    allowAdminWaitlistBypass,
    autoCheckInTodayBoards,
  });

  return response.data;
});

export const createPayment = createAsyncThunk<
  any,
  string,
  { state: RootState }
>("player/createPayment", async (_, { getState, dispatch }) => {
  const selectedBoardIds = getState().player.selectedBoardIds;
  const playerId = getState().player.player?.id;
  const licenceId = getState().player.player?.licenceId;

  const getPaymentUrl = httpsCallable<unknown, any>(
    cloudFunctions,
    "getPaymentUrl"
  );

  const tournamentId = getState().tournoi.tournoi?.id;

  try {
    const origin = window.location.origin;
    const response = await getPaymentUrl({
      licenceId,
      playerId,
      tournamentId,
      selectedBoardIds,
      success_url: `${origin}/register/thanks?session_id={CHECKOUT_SESSION_ID}"`,
      cancel_url: origin,
    });

    window.location.href = response.data.url;
  } catch (error: unknown) {
    throw error;
  }
});

export type BalancePaymentLinkResult = {
  url: string;
  amountEuros: number;
  totalEuros: number;
  paidEuros: number;
};

/** Génère un Checkout Stripe au montant du reste à payer (tous tableaux du joueur). */
export const createBalancePaymentLink = createAsyncThunk<
  BalancePaymentLinkResult,
  { playerId: string },
  { state: RootState }
>("player/createBalancePaymentLink", async ({ playerId }, { getState }) => {
  const tournamentId = getState().tournoi.tournoi?.id;
  if (!tournamentId) {
    throw new Error("Tournoi courant introuvable.");
  }
  if (!playerId) {
    throw new Error("Joueur introuvable.");
  }

  const getBalancePaymentUrl = httpsCallable<
    {
      playerId: string;
      tournamentId: string;
      success_url: string;
      cancel_url: string;
    },
    BalancePaymentLinkResult & {
      unpaidBoards?: Array<{ id: string; name: string }>;
      paidBoards?: Array<{ id: string; name: string }>;
    }
  >(cloudFunctions, "getBalancePaymentUrl");

  const origin = window.location.origin;
  const response = await getBalancePaymentUrl({
    playerId,
    tournamentId,
    success_url: `${origin}/register/thanks?session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: origin,
  });

  const data = response.data;
  if (!data?.url || typeof data.amountEuros !== "number") {
    throw new Error("Réponse de paiement invalide.");
  }

  return {
    url: data.url,
    amountEuros: data.amountEuros,
    totalEuros: data.totalEuros,
    paidEuros: data.paidEuros,
  };
});

export const checkInParticipations = createAsyncThunk<
  { participationIds: string[]; checkedInAtMs: number },
  string[],
  { state: RootState }
>(
  "player/checkInParticipations",
  async (participationIds, { getState }) => {
    const db = firestoreInstance;
    const now = Timestamp.now();
    const tournamentId = getState().tournoi.tournoi?.id;
    if (!tournamentId) {
      throw new Error("Tournoi courant introuvable.");
    }

    await Promise.all(
      participationIds.map(async (id) => {
        const participationRef = doc(
          db,
          "tournois",
          tournamentId,
          "participation",
          id
        );
        await updateDoc(participationRef, {
          checkedIn: true,
          checkedInAt: now,
        });
      })
    );

    // Pas de reload complet : le reducer met à jour playersWithRegistrations
    return {
      participationIds,
      checkedInAtMs: now.toMillis(),
    };
  }
);

export const uncheckInParticipation = createAsyncThunk<
  { participationId: string },
  string,
  { state: RootState }
>(
  "player/uncheckInParticipation",
  async (participationId, { getState }) => {
    const db = firestoreInstance;
    const tournamentId = getState().tournoi.tournoi?.id;
    if (!tournamentId) {
      throw new Error("Tournoi courant introuvable.");
    }
    const participationRef = doc(
      db,
      "tournois",
      tournamentId,
      "participation",
      participationId
    );
    await updateDoc(participationRef, {
      checkedIn: false,
      checkedInAt: null,
    });
    return { participationId };
  }
);

export const fetchPlayerById = createAsyncThunk<
  any,
  string,
  { state: RootState }
>("player/fetchPlayerById", async (playerId: string, thunkApi) => {
  const playerDoc = await getDoc(doc(firestoreInstance, "players", playerId));
  if (!playerDoc.exists()) return null;
  return { ...playerDoc.data(), id: playerDoc.id };
});

/**
 * Synchronise le state selectedBoardIds avec les vraies participations du joueur
 * Utile pour éviter les désynchronisations après sauvegarde
 */
export const syncSelectedBoardsWithParticipations = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("player/syncSelectedBoards", async (_, { getState, dispatch }) => {
  const state = getState();
  const player = state.player.player;
  const participations = player?.participation || [];

  // Récupérer les IDs des tableaux où le joueur est réellement inscrit
  const actualBoardIds = participations.map((p) => p.tableauId);

  // Ne garder que les nouvelles sélections (pas les tableaux déjà inscrits)
  const validSelectedIds = state.player.selectedBoardIds.filter(
    (id) => !actualBoardIds.includes(id)
  );

  // Mettre à jour le state
  dispatch(playerSlice.actions.updateSelectedBoards(validSelectedIds));
});

const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    cleanState: (state: PlayerState) => {
      state.selectedBoardIds = [];
      state.unselectedBoardIds = [];
      state.allowAdminDailyLimitBypass = false;
      state.allowAdminWaitlistBypass = false;
    },

    updateSelectedBoards: (
      state: PlayerState,
      action: PayloadAction<string[]>
    ) => {
      state.selectedBoardIds = action.payload;
    },

    setAllowAdminDailyLimitBypass: (
      state: PlayerState,
      action: PayloadAction<boolean>
    ) => {
      state.allowAdminDailyLimitBypass = action.payload;
    },

    setAllowAdminWaitlistBypass: (
      state: PlayerState,
      action: PayloadAction<boolean>
    ) => {
      state.allowAdminWaitlistBypass = action.payload;
    },

    switchState: (state: PlayerState, boardId: PayloadAction<string>) => {
      const id = boardId.payload;
      const isSelected =
        state.selectedBoardIds.find((boardId) => id === boardId) !== undefined;
      const isUnSelected =
        state.unselectedBoardIds.find((boardId) => id === boardId) !==
        undefined;

      const isAlreadyRegistered =
        state.player?.participation?.find((p) => p.tableauId === id) !==
        undefined;

      let nextSelected = [...state.selectedBoardIds];
      let nextUnselected = [...state.unselectedBoardIds];

      if (!isAlreadyRegistered && !isSelected) {
        nextSelected.push(id);

        const boardDate = state.accessibleBoards.find((b) => b.id === id)?.date;
        const mandatoryBoardWithDate = state.accessibleBoards.find(
          (b) => b.id !== id && b.obligatoire && b.date === boardDate
        );

        if (
          mandatoryBoardWithDate &&
          !mandatoryBoardWithDate.ferme &&
          !mandatoryBoardWithDate.isFull &&
          !isRegistered(mandatoryBoardWithDate.id, state)
        ) {
          nextSelected.push(mandatoryBoardWithDate.id as string);
        }
      } else if (!isAlreadyRegistered && isSelected) {
        nextSelected = nextSelected.filter((boardId) => boardId !== id);
      } else if (isAlreadyRegistered && !isUnSelected) {
        nextUnselected.push(id);
      } else if (isAlreadyRegistered && isUnSelected) {
        nextUnselected = nextUnselected.filter((boardId) => boardId !== id);
      }

      const candidate: PlayerState = {
        ...state,
        selectedBoardIds: nextSelected,
        unselectedBoardIds: nextUnselected,
      };
      const effective = getEffectiveSelectedBoardIds(candidate);
      const shouldValidateDailyLimit = !state.allowAdminDailyLimitBypass;
      if (
        shouldValidateDailyLimit &&
        !isDailySelectionValid(effective, state.accessibleBoards)
      ) {
        return;
      }
      if (hasIncompatiblePairAmong(effective, state.accessibleBoards)) {
        return;
      }

      state.selectedBoardIds = nextSelected;
      state.unselectedBoardIds = nextUnselected;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayerFromLicence.pending, (state: PlayerState, action) => {
        state.status = "loading";
      })
      .addCase(
        fetchPlayerFromLicence.fulfilled,
        (state: PlayerState, action: PayloadAction<Players>) => {
          if (!action.payload) {
            state.status = "noplayer";
            state.player = {} as Players;
            state.error =
              "Numéro de licence non trouvé, réessayez avec un autre ou contactez-nous par mail sur tournoi@sqyping.fr";
          } else {
            state.status = "succeeded";
            state.player = action.payload;
            // Nettoyer les sélections lors du changement de joueur
            state.selectedBoardIds = [];
            state.unselectedBoardIds = [];
            state.allowAdminDailyLimitBypass = false;
            state.allowAdminWaitlistBypass = false;
          }
        }
      )
      .addCase(
        fetchPlayerFromLicence.rejected,
        (state: PlayerState, action) => {
          state.status = "failed";
          if (action.error.message) {
            state.error = action.error.message;
          }
        }
      )
      .addCase(fetchPlayersWithRegistrations.pending, (state: PlayerState) => {
        state.playersWithRegistrations = [];
        state.playersWithRegistrationsLoading = true;
      })
      .addCase(
        fetchPlayersWithRegistrations.fulfilled,
        (state: PlayerState, action: PayloadAction<Players[]>) => {
          state.playersWithRegistrations = action.payload;
          state.playersWithRegistrationsLoading = false;
        }
      )
      .addCase(checkInParticipations.fulfilled, (state, action) => {
        const idSet = new Set(action.payload.participationIds);
        const checkedInAt = new Date(action.payload.checkedInAtMs);
        for (const player of state.playersWithRegistrations) {
          for (const participation of player.participation ?? []) {
            if (participation.id != null && idSet.has(String(participation.id))) {
              participation.checkedIn = true;
              participation.checkedInAt = checkedInAt;
            }
          }
        }
      })
      .addCase(uncheckInParticipation.fulfilled, (state, action) => {
        const targetId = action.payload.participationId;
        for (const player of state.playersWithRegistrations) {
          for (const participation of player.participation ?? []) {
            if (String(participation.id) === targetId) {
              participation.checkedIn = false;
              participation.checkedInAt = undefined;
            }
          }
        }
      })
      .addCase(fetchPlayers.pending, (state: PlayerState) => {
        state.players = [];
        state.playersWithRegistrationsLoading = true;
      })
      .addCase(
        fetchPlayers.fulfilled,
        (state: PlayerState, action: PayloadAction<Players[]>) => {
          state.players = action.payload;
          state.playersWithRegistrationsLoading = false;
        }
      )
      .addCase(fetchPlayers.rejected, (state: PlayerState, action) => {
        state.players = [];
        state.playersWithRegistrationsLoading = false;
        if (action.error.message) {
          state.error = action.error.message;
        }
      })
      .addCase(fetchPlayerById.pending, (state: PlayerState) => {
        state.status = "loading";
      })
      .addCase(
        fetchPlayerById.fulfilled,
        (state: PlayerState, action: PayloadAction<Players | null>) => {
          if (!action.payload) {
            state.status = "noplayer";
            state.player = {} as Players;
            state.error = "Aucun joueur trouvé pour cet identifiant.";
          } else {
            state.status = "succeeded";
            state.player = action.payload;
            // Nettoyer les sélections lors du changement de joueur
            state.selectedBoardIds = [];
            state.unselectedBoardIds = [];
            state.allowAdminDailyLimitBypass = false;
            state.allowAdminWaitlistBypass = false;
          }
        }
      )
      .addCase(fetchPlayerById.rejected, (state: PlayerState, action) => {
        state.status = "failed";
        if (action.error.message) {
          state.error = action.error.message;
        }
      });
    builder
      .addCase(
        fetchAccessibleBoards.fulfilled,
        (state: PlayerState, action: PayloadAction<Tableau[]>) => {
          state.status = "succeeded";
          state.accessibleBoards = action.payload.map((board) => board);
        }
      )
      .addCase(fetchAccessibleBoards.pending, (state: PlayerState, action) => {
        state.status = "loading";
      })
      .addCase(fetchAccessibleBoards.rejected, () => {
        // Error handled by component
      });

    builder
      .addCase(createPayment.fulfilled, (state: PlayerState) => {
        state.paymentStatus = "succeeded";
        // Nettoyer les sélections après paiement réussi
        state.selectedBoardIds = [];
        state.unselectedBoardIds = [];
      })
      .addCase(createPayment.pending, (state: PlayerState) => {
        state.paymentStatus = "loading";
      })
      .addCase(saveRegistrations.pending, (state: PlayerState) => {
        state.saveRegistrationsStatus = "loading";
      })
      .addCase(saveRegistrations.fulfilled, (state: PlayerState) => {
        state.saveRegistrationsStatus = "idle";
        // Nettoyer les sélections après sauvegarde réussie
        state.selectedBoardIds = [];
        state.unselectedBoardIds = [];
        state.allowAdminWaitlistBypass = false;
      })
      .addCase(saveRegistrations.rejected, (state: PlayerState) => {
        state.saveRegistrationsStatus = "failed";
      });
  },
});

export default playerSlice.reducer;

export const {
  switchState,
  cleanState,
  updateSelectedBoards,
  setAllowAdminDailyLimitBypass,
  setAllowAdminWaitlistBypass,
} = playerSlice.actions;

export const fetchedPlayer = (state: RootState) => state.player.player;
export const getPlayers = (state: RootState) => state.player.players;
export const fetchedNoPlayer = (state: RootState) =>
  state.player.status === "noplayer";
export const fetchedErrorMessage = (state: RootState) => state.player.error;
export const fetchedSearchError = (state: RootState) =>
  state.player.status === "failed";

export const accessibleBoardsByDate = (state: RootState) => {
  const accessibleBoards: AccessibleBoard[] = state.player.accessibleBoards
    .map(
      (board) =>
        ({
          ...board,
          selected: isRegistered(board.id, state.player),
          disabled: isDisabled(
            board.id,
            state.player,
            state.session.admin,
            state.player.allowAdminDailyLimitBypass
          ),
        } as AccessibleBoard)
    )
    .sort((a, b) => a.name.localeCompare(b.name, "fr"));

  // Jour civil (pas l’heure) pour un seul bloc / jour, tableaux A→Z dedans.
  return groupBy(accessibleBoards, (item) => calendarDayKey(item.date));
};

export const prices = (state: RootState) => {
  const selectedBoardIds = state.player.selectedBoardIds;
  const unselectedBoardIds = state.player.unselectedBoardIds;
  const participations = state.player.player?.participation;

  // recuperer les anciens tableaux
  const formerBoardsIds = participations?.map((p) => p.tableauId) || [];

  // recupérer les tableaux existants + ceux sélectionnés - les déselectionnés
  const allSelectedBoardIds = [...formerBoardsIds, ...selectedBoardIds].filter(
    (id) => !unselectedBoardIds.includes(id)
  );

  const pricingTag = state.tournoi.tournoi?.pricingTag;
  return {
    formerPrice: calculatePrice(
      formerBoardsIds,
      state.player.accessibleBoards,
      pricingTag
    ),
    newPrice: calculatePrice(
      allSelectedBoardIds,
      state.player.accessibleBoards,
      pricingTag
    ),
    priceDifference:
      calculatePrice(
        allSelectedBoardIds,
        state.player.accessibleBoards,
        pricingTag
      ) -
      calculatePrice(
        formerBoardsIds,
        state.player.accessibleBoards,
        pricingTag
      ),
  };
};

const calculatePrice = (
  boardIds: string[],
  boards: Tableau[],
  pricingTag?: string | null
) => {
  // IMPORTANT métier:
  // La majoration "après date limite" affichée sur la Home (supplementApresDate)
  // est informative uniquement et ne doit pas impacter ce calcul.
  // Grille : `constants/tournamentPricing.ts` (tableaux `offert` exclus).
  const selected = boards.filter((board) => boardIds.includes(board.id));
  return registrationPriceFromBoards(selected, pricingTag);
};

export const hasCurrentActions = (state: RootState): boolean => {
  return (
    state.player.selectedBoardIds.length +
      state.player.unselectedBoardIds.length >
    0
  );
};

export const hasRegistrations = (state: RootState): boolean => {
  return (
    state.player.player?.participation !== undefined &&
    state.player.player?.participation?.length > 0
  );
};

export const getAccessibleBoards = (state: RootState) =>
  state.player.accessibleBoards;
export const getAllowAdminDailyLimitBypass = (state: RootState) =>
  state.player.allowAdminDailyLimitBypass;

export const getAllowAdminWaitlistBypass = (state: RootState) =>
  state.player.allowAdminWaitlistBypass;

export const getPlayerLoading = (state: RootState) =>
  state.player.status === "loading";

export const getPaymentLoading = (state: RootState) =>
  state.player.paymentStatus === "loading";

export const getSaveRegistrationsLoading = (state: RootState) =>
  state.player.saveRegistrationsStatus === "loading";

export const getPlayersWithRegistrationsLoading = (state: RootState) =>
  state.player.playersWithRegistrationsLoading;

export const getPlayersLoading = (state: RootState) =>
  state.player.playersWithRegistrationsLoading;

export const getPlayersWithRegistrations = (state: RootState) =>
  state.player.playersWithRegistrations
    .filter((player: Players) => {
      // si aucune participation on filtre le joueur
      if (!player.participation || player.participation.length === 0) {
        return false;
      }

      // on filtre les participations sans tableau dans le tournoi courant
      const tempParticipations = player.participation.filter(
        (participation: Participation) => {
          if (!participation.tableau) {
            return false;
          }
          return true;
        }
      );

      // on vérifie à nouveau s'il reste des participations
      if (!tempParticipations || tempParticipations.length === 0) {
        return false;
      }

      // on garde le joueur
      return true;
    })
    .sort((p1, p2) => {
      const fullName1 = p1.name + " " + p1.surname;
      const fullName2 = p2.name + " " + p2.surname;
      if (fullName1 === fullName2) {
        if (
          p1!.participation![0].tableau!.name <
          p2!.participation![0].tableau!.name
        ) {
          return -1;
        }
        return 1;
      }
      if (fullName1 < fullName2) {
        return -1;
      }
      return 1;
    });
